/* eslint-disable no-underscore-dangle */
/**
 * "Waiting Times" — the free-waiting terms agreed with one sales channel.
 *
 * Its own modal rather than three boxes buried in the partner form, because
 * whoever fills it in is agreeing to something on the company's behalf and
 * has to be able to see what. The rules are spelled out above the boxes:
 * which allowance applies where, what the clock is anchored to, and what a
 * late flight does to it. A number typed without those in view is a number
 * typed blind.
 *
 * The two things it has to keep straight:
 *
 *  - AN EMPTY BOX IS NOT A ZERO. Empty means "nothing agreed, use the house
 *    default" — shown as the placeholder so it is never a mystery. A typed 0
 *    means "this channel gives no free waiting at all", which is a real
 *    term and is kept. `waitingTimesPayload` is the one rule for that, and
 *    the API applies the same one on the way in (`configuredMinutes`).
 *  - IT IS THE PICKUP ADDRESS that decides airport-or-not, not the drop-off.
 *    A transfer TO the airport carries a flight number and still gets the
 *    short allowance, because the driver is waiting outside a hotel.
 *
 * The house numbers are never written here. They come from the API on the
 * partner row (`free_waiting_policy.house`), so this screen and the driver's
 * countdown cannot disagree about what the default is.
 *
 * The rows are driven by WAITING_TIME_FIELDS, so a fourth term is one more
 * entry in that table rather than a new layout.
 */
import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { post } from '../../../Services/API';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import {
  WAITING_TIME_FIELDS,
  houseWaitingTimes,
  waitingTimesPayload,
  waitingTimesPolicy,
} from '../../../Utils/freeWaiting';

/** The partner's 0/1 flags, re-cast, and only the ones the row carries. */
const numericFlags = (partner) => Object.fromEntries(
  ['active', 'ph_branding_allowed', 'driver_portal_enabled']
    .filter((key) => partner?.[key] !== undefined && partner?.[key] !== null)
    .map((key) => [key, Number(partner[key])]),
);

export default function PartnerWaitingTimesModal({
  originalPartner = {}, show = false, handleClose, fetchPartners,
}) {
  const [partner, setPartner] = useState(originalPartner);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const house = houseWaitingTimes(originalPartner);
  const policy = waitingTimesPolicy(originalPartner);

  /**
   * The minutes boxes keep the RAW string, deliberately.
   *
   * The shared `setStateFromInput` casts a `type="number"` input with
   * `Number(e.target.value)`, and `Number('')` is 0 — so an emptied box
   * would land in state as a configured zero and be saved as "this channel
   * gives no free waiting at all". The empty string has to survive as far
   * as `waitingTimesPayload`, which is the one place that decides what an
   * empty box means.
   */
  const handleChange = (e) => {
    const { id, value } = e.target;
    setPartner((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // The whole partner goes back, not just these three: the upsert treats
      // an omitted field as deliberately cleared, so posting the boxes alone
      // would wipe the partner's billing, queues and flags.
      //
      // The 0/1 flags are re-cast only where the row HAS them — `Number(undefined)`
      // is NaN, which the schema rejects, and a partner would become
      // unsaveable from this screen over a field it does not use.
      await post('/bookings/partners', {
        ...partner,
        ...numericFlags(partner),
        ...waitingTimesPayload(partner),
      });
      fetchPartners();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message);

      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Waiting Times
          {partner.name ? ` — ${partner.name}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>

        <Error error={error} setError={setError} />

        {/* The rules, before the boxes. Someone setting these is agreeing to
            them on the company's behalf and should be able to read what they
            are without leaving the screen. */}
        <div className="bg-body-secondary rounded p-3 mb-4 small" data-testid="waiting-times-rules">
          <p className="mb-2">
            <strong>Free waiting</strong>
            {' '}
            is how long the driver waits at the pickup before the wait becomes
            billable overtime. These are
            {' '}
            <strong>this channel&apos;s commercial terms</strong>
            , not the operator&apos;s — the same operator driving a job for
            this partner and a Pickup Hungary direct job is on two different
            agreements.
          </p>
          <ul className="mb-2 ps-3">
            <li>
              The clock runs from the
              {' '}
              <strong>scheduled pickup time</strong>
              , not from when the driver arrives — so arriving early does not
              burn the customer&apos;s allowance.
            </li>
            <li>
              The
              {' '}
              <strong>pickup address</strong>
              {' '}
              decides which allowance applies. A transfer
              {' '}
              <em>to</em>
              {' '}
              an airport gets the &quot;everywhere else&quot; number: the
              driver is waiting outside a hotel.
            </li>
            <li>
              A late arriving flight slides the whole window later by the
              delay, up to the cap below. Past the cap the driver is told to
              check with the office.
            </li>
          </ul>
          <p className="mb-0">
            <strong>Leave a box empty</strong>
            {' '}
            to use the house default (shown greyed in each box).
            {' '}
            <strong>Type 0</strong>
            {' '}
            only to agree that this channel gives
            {' '}
            <em>no</em>
            {' '}
            free waiting at all. They are not the same thing.
          </p>
        </div>

        {policy && !policy.configured && (
          <div className="alert alert-warning py-2 small" data-testid="waiting-times-unset">
            Nothing has been agreed for this partner yet, so its bookings run
            on the house defaults.
          </div>
        )}

        <Form>
          {WAITING_TIME_FIELDS.map(({
            field, policyKey, label, hint,
          }) => {
            const houseValue = house?.[policyKey];

            return (
              <Form.Group className="mb-3 col-12" controlId={field} key={field}>
                <Form.Label>
                  {label}
                  {' '}
                  <span className="text-muted fw-normal">(minutes)</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step={1}
                  // The default, from the API — never a number typed here.
                  placeholder={houseValue === undefined ? '' : String(houseValue)}
                  value={partner[field] ?? ''}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  {hint}
                  {houseValue === undefined ? '' : ` Empty = the house default of ${houseValue}.`}
                </Form.Text>
                <FormControlFeedback errors={validationErrors} field={field} />
              </Form.Group>
            );
          })}
        </Form>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            Save Waiting Times
          </Button>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  );
}
